import { Button, Icon, Progress, Tag } from "@app/ui";
import { useT, useFormatDateTime } from "../i18n.js";
import { displayNameOf, initialsOf } from "./identity.js";
import type { PublicUser } from "../types.js";

/**
 * Identity band. Answers "who am I signed in as, and is this account safe?"
 * before any settings appear — the two questions a profile page exists for.
 */

export interface ProfileHeroProps {
  user: PublicUser;
  /** Completed / total security checks; the ring reads as a percentage. */
  score: { done: number; total: number };
  onVerifyEmail: () => void;
  onDownloadData: () => void;
  downloading: boolean;
}

export function ProfileHero({ user, score, onVerifyEmail, onDownloadData, downloading }: ProfileHeroProps) {
  const t = useT();
  const fmtDate = useFormatDateTime();

  const percent = score.total === 0 ? 0 : Math.round((score.done / score.total) * 100);
  const verdict = percent === 100 ? "scoreStrong" : percent >= 60 ? "scoreFair" : "scoreWeak";

  return (
    <section className="pf-hero">
      <div className="pf-hero__id">
        <span className="pf-avatar" aria-hidden="true">
          {initialsOf(user.displayName, user.email)}
        </span>
        <div className="pf-hero__lines">
          <h2 className="pf-hero__name">{displayNameOf(user.displayName, user.email)}</h2>
          <div className="pf-hero__mail">
            <Icon name="mail" size={14} />
            {user.email}
            {user.emailVerified ? (
              <Tag color="success">{t("profile.emailVerified")}</Tag>
            ) : (
              <>
                <Tag color="warning">{t("profile.emailUnverified")}</Tag>
                <Button size="sm" variant="subtle" onClick={onVerifyEmail}>
                  {t("profile.verifyNow")}
                </Button>
              </>
            )}
          </div>
          <div className="pf-hero__tags">
            {/* A role is a fact, not a status — it stays on the neutral pill so the
                only colour left in the band is the warning you can act on. */}
            {user.roles.map((r) => (
              <Tag key={r}>{r}</Tag>
            ))}
            {user.demo && <Tag color="warning">{t("profile.demoAccount")}</Tag>}
            <span className="pf-hero__fact">
              <Icon name="calendar" size={12} />
              {t("profile.memberSince", { date: fmtDate(user.createdAt) })}
            </span>
          </div>
        </div>
      </div>

      <div className="pf-hero__aside">
        <div className="pf-score">
          {/* Ink, not a hue: the band is identity chrome and a red/green ring here
              shouted louder than the checklist that can actually be acted on. */}
          <Progress type="circle" percent={percent} status="neutral" size={68} strokeWidth={6} />
          <div className="pf-score__text">
            <span className="pf-score__label">{t("profile.securityScore")}</span>
            <span className="pf-score__value">{t(`profile.${verdict}`)}</span>
            <span className="pf-score__label">
              {t("profile.checklistDone", { done: score.done, total: score.total })}
            </span>
          </div>
        </div>
        <div className="pf-hero__actions">
          <Button
            size="sm"
            icon={<Icon name="download" size={14} />}
            loading={downloading}
            onClick={onDownloadData}
          >
            {t("dashboard.downloadData")}
          </Button>
        </div>
      </div>
    </section>
  );
}
